// Rungs 3–5 of the ladder (plan §4), derived from stated principles and
// cited; none copies a figure. Every move still replaces ONE authored chord
// by one or more inside its own span — the seam Phase 0 built — which is
// why the structural rung is the pedal point and the turnback rather than
// displacement across bars (a layer cannot move a barline; see plan §9).
//
// Rung 3 — Passing and approach chords (Felts ch. 11–12; Barry Harris):
//   passing diminished between chords a step apart, the diminished-for-
//   dominant swap both ways (the °7's V, its subV, plain or with the ♭9),
//   the chromatic approach chord, Barry Harris's 6th/diminished alternation
//   over a static chord, and the line clichés — descending and ascending
//   inner lines, the Aeolian bass version and the major-key cliché, one
//   chord per beat as the book writes them.
// Rung 4 — Modal interchange (Felts ch. 5; Berklee Book of Jazz Harmony
//   "Modal interchange"): Felts's twelve borrowed chords, each keyed to the
//   diatonic chord it replaces (I → Im7/Im6/Im∆7, ii → IIø/♭II∆ …), plus any
//   borrowed chord that LINKS into the next chord by strong root motion —
//   and, from Felts ch. 8, the turnaround catalogue a final tonic bar can
//   carry, written one chord per beat inside the bar.
// Rung 5 — Bass line (Felts ch. 6, reharmonization using bass lines): a
//   stepwise bass written from the chord into the next chord's root and
//   harmonised note by note — the chord over its 3rd/5th (its 7th only
//   straight after root position), the diatonic chord rooted there, or a
//   dominant rooted there that cadences on — and the pedal point, a held
//   chord re-heard as two chords over its own bass.

import { CHORD_TYPES, type ChordTypeId } from '../chords';
import { qualityFamily } from '../corpus';
import { pitchClassOf, spellNote } from '../notes';
import {
  dealBeats,
  diatonicSevenths,
  hasSlashBass,
  heard,
  heardFromBass,
  isDominant,
  isSus,
  mod12,
  proposal,
  regionAt,
  restate,
  spellRoot,
  split,
  symbolOf,
  type Proposal,
} from './helpers';
import type { ReharmChordSpec, ReharmMove } from './types';

const isMinorFamily = (type: ChordTypeId) => qualityFamily(type) === 'm';
const isMajorFamily = (type: ChordTypeId) => qualityFamily(type) === '∆';

// ---------------------------------------------------------------------------
// Rung 3
// ---------------------------------------------------------------------------

const passingDim: ReharmMove = {
  id: 'passing-dim',
  rung: 3,
  name: 'Passing diminished',
  hint: 'A diminished chord between two chords a step apart.',
  source: 'Felts ch. 12 (diminished seventh chords); Berklee Book of Jazz Harmony ch. 7.',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const next = ctx.sequence[at + 1];
    if (!next || ctx.chordBeats[at] < 2 || heardFromBass(ctx, at)) return [];
    const step = mod12(heard(ctx, at + 1).rootPc - pitchClassOf(chord.root));
    // Rising a whole step: the sharpened root's °7 fills the gap (I ♯I° ii);
    // falling a whole step: the flattened root's °7 (iii ♭III° ii).
    const dimPc =
      step === 2
        ? mod12(pitchClassOf(chord.root) + 1)
        : step === 10
          ? mod12(pitchClassOf(chord.root) - 1)
          : null;
    if (dimPc === null) return [];
    // A rising passing chord is the SHARPENED root (C∆ C♯°7 Dm7), a falling
    // one the flattened (Dm7 D♭°7 C∆) — the letter says which way it moves.
    const root = step === 2 ? spellNote(dimPc, 'sharp') : spellNote(dimPc, 'flat');
    const [a, b] = split(ctx, at, 2);
    return [
      proposal(
        ctx,
        passingDim,
        at,
        [restate(chord, a), { root, chordType: 'dim7', beats: b }],
        `${symbolOf({ root, chordType: 'dim7', beats: 0 })} passes to ${next.symbol}`,
      ),
    ];
  },
};

const dimForDominant: ReharmMove = {
  id: 'dim-for-dom',
  rung: 3,
  name: 'Diminished for dominant',
  hint: 'The °7 on the 3rd of a dominant is its 7♭9 without the root.',
  source: 'Felts ch. 12; Barry Harris (the dominant and its diminished).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    if (!isDominant(chord) || isSus(chord) || heardFromBass(ctx, at)) return [];
    const region = regionAt(ctx, at);
    const root = spellRoot(mod12(pitchClassOf(chord.root) + 4), region);
    return [
      proposal(
        ctx,
        dimForDominant,
        at,
        [{ root, chordType: 'dim7', beats: ctx.chordBeats[at] }],
        `${chord.symbol} without its root`,
      ),
    ];
  },
};

const dominantForDim: ReharmMove = {
  id: 'dom-for-dim',
  rung: 3,
  name: 'Dominant for diminished',
  hint: 'A °7 is a 7♭9 whose root sits a major 3rd below.',
  source: 'Felts ch. 12 (converting diminished sevenths to dominants).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    if (chord.chordType !== 'dim7' || heardFromBass(ctx, at)) return [];
    const region = regionAt(ctx, at);
    const next = ctx.sequence[at + 1];
    // Four dominants share the set. Felts: prefer the one that makes a
    // dominant cadence into what follows — its V7 OR its subV7, "the
    // standard choices" — and only where neither exists take the M3-below.
    const roots = [0, 3, 6, 9].map((k) => mod12(pitchClassOf(chord.root) - 4 + k));
    const nextPc = next ? heard(ctx, at + 1).rootPc : undefined;
    const fifth = nextPc === undefined ? undefined : roots.find((pc) => mod12(nextPc - pc) === 5);
    const subV = nextPc === undefined ? undefined : roots.find((pc) => mod12(pc - nextPc) === 1);
    const beats = ctx.chordBeats[at];
    const out: Proposal[] = [];
    const offer = (pc: number, type: ChordTypeId, why: string) => {
      const root = spellRoot(pc, region);
      out.push(proposal(ctx, dominantForDim, at, [{ root, chordType: type, beats }], why));
    };
    if (fifth !== undefined && next) {
      offer(fifth, 'dom7b9', `V7♭9 into ${next.symbol}`);
      offer(fifth, 'dom7', `V7 into ${next.symbol}`);
    }
    if (subV !== undefined && next) offer(subV, 'dom7', `the tritone sub into ${next.symbol}`);
    if (fifth === undefined && subV === undefined) {
      offer(roots[0], 'dom7b9', `the dominant under ${chord.symbol}`);
      offer(roots[0], 'dom7', `the dominant under ${chord.symbol}, plain`);
    }
    return out;
  },
};

const chromaticApproach: ReharmMove = {
  id: 'chromatic-approach',
  rung: 3,
  name: 'Chromatic approach',
  hint: 'The same chord a half step above or below, just before it.',
  source: 'Felts ch. 3 and 11 (approach chords); Willmott, chromatic guide lines.',
  candidates(ctx, at) {
    const target = ctx.sequence[at];
    const prev = ctx.sequence[at - 1];
    // The target's own quality a half step away: a hybrid target has no one
    // quality to slide in on.
    if (!prev || ctx.chordBeats[at - 1] < 2 || heardFromBass(ctx, at)) return [];
    const prevPc = heard(ctx, at - 1).rootPc;
    if (mod12(prevPc - pitchClassOf(target.root)) === 1) return [];
    if (mod12(pitchClassOf(target.root) - prevPc) === 1) return [];
    const region = regionAt(ctx, at);
    const [a, b] = split(ctx, at - 1, 2);
    const out: Proposal[] = [];
    for (const [offset, where] of [
      [1, 'above'],
      [-1, 'below'],
    ] as const) {
      const root = spellRoot(mod12(pitchClassOf(target.root) + offset), region);
      out.push(
        proposal(
          ctx,
          chromaticApproach,
          at - 1,
          [restate(prev, a), { root, chordType: target.chordType, beats: b }],
          `slides into ${target.symbol} from a half step ${where}`,
        ),
      );
    }
    return out;
  },
};

const sixthDiminished: ReharmMove = {
  id: 'sixth-dim',
  rung: 3,
  name: 'Sixth and diminished',
  hint: 'Barry Harris: a held chord alternates with the °7 a step above its root.',
  source: 'Barry Harris, the 6th-diminished scale (major and minor).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    if (ctx.chordBeats[at] < 4 || hasSlashBass(chord)) return [];
    const sixth: ChordTypeId | null = isMajorFamily(chord.chordType)
      ? 'maj6'
      : isMinorFamily(chord.chordType) && !chord.chordType.startsWith('min7b5')
        ? 'min6'
        : null;
    if (!sixth) return [];
    const region = regionAt(ctx, at);
    const dimRoot = spellRoot(mod12(pitchClassOf(chord.root) + 2), region);
    const [a, b, c, d] = [0, 1, 2, 3].map(() => ctx.chordBeats[at] / 4);
    return [
      proposal(
        ctx,
        sixthDiminished,
        at,
        [
          { root: chord.displayRoot, chordType: sixth, beats: a },
          { root: dimRoot, chordType: 'dim7', beats: b },
          { root: chord.displayRoot, chordType: sixth, beats: c },
          { root: dimRoot, chordType: 'dim7', beats: d },
        ],
        `${symbolOf({ root: chord.displayRoot, chordType: sixth, beats: 0 })} and ${symbolOf({ root: dimRoot, chordType: 'dim7', beats: 0 })} in turn`,
      ),
    ];
  },
};

/** The line-cliché shapes (Felts ch. 11), each a held chord with ONE voice
 *  walking, written one chord per beat. Minor: the descending 1–7–♭7–6 inner
 *  line, the ascending 5–♯5–6–♯5, and the Aeolian version — the SAME minor
 *  chord over a bass stepping down 1–♭7–♭6–5 (the `bass` field is what makes
 *  it sound). Major: 5–♯5–6–♯5 over a major chord. */
type ClicheStep = { type: ChordTypeId; bassOffset?: number };
const MINOR_CLICHES: { steps: ClicheStep[]; why: string }[] = [
  {
    steps: [{ type: 'min' }, { type: 'minmaj7' }, { type: 'min7' }, { type: 'min6' }],
    why: 'inner voice walks 1, 7, ♭7, 6',
  },
  {
    steps: [{ type: 'min' }, { type: 'min7s5' }, { type: 'min6' }, { type: 'min7s5' }],
    why: 'inner voice walks 5, ♯5, 6, ♯5',
  },
  {
    steps: [
      { type: 'min' },
      { type: 'min', bassOffset: 10 },
      { type: 'min', bassOffset: 8 },
      { type: 'min', bassOffset: 7 },
    ],
    why: 'bass walks down 1, ♭7, ♭6, 5 under the chord',
  },
];
const MAJOR_CLICHES: { steps: ClicheStep[]; why: string }[] = [
  {
    steps: [{ type: 'maj' }, { type: 'maj7s5' }, { type: 'maj6' }, { type: 'maj7s5' }],
    why: 'inner voice walks 5, ♯5, 6, ♯5',
  },
];

const lineCliche: ReharmMove = {
  id: 'line-cliche',
  rung: 3,
  name: 'Line cliché',
  hint: 'The chord holds while one voice walks.',
  source: 'Felts ch. 11 (line clichés).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    // The line walks against the chord's own root, and the Aeolian shape IS
    // a bass line: a chord with a written bass already has one.
    if (ctx.chordBeats[at] < 4 || hasSlashBass(chord)) return [];
    const shapes = isMajorFamily(chord.chordType)
      ? MAJOR_CLICHES
      : isMinorFamily(chord.chordType) && !chord.chordType.startsWith('min7b5')
        ? MINOR_CLICHES
        : [];
    const region = regionAt(ctx, at);
    const rootPc = pitchClassOf(chord.root);
    const beats = dealBeats(ctx, at, 4);
    return shapes.map(({ steps, why }) =>
      proposal(
        ctx,
        lineCliche,
        at,
        steps.map((step, i) => ({
          root: chord.displayRoot,
          chordType: step.type,
          ...(step.bassOffset !== undefined
            ? { bass: spellRoot(mod12(rootPc + step.bassOffset), region) }
            : {}),
          beats: beats[i],
        })),
        why,
      ),
    );
  },
};

// ---------------------------------------------------------------------------
// Rung 4
// ---------------------------------------------------------------------------

/** Felts's twelve modal-interchange chords for a major key (ch. 5), keyed by
 *  the diatonic chord each REPLACES — "the goal is to expand the colour", so
 *  the function need not survive. Degrees from the tonic. */
type Borrowed = { degree: number; type: ChordTypeId; roman: string };
type Diatonic = { degree: number; family: ReturnType<typeof qualityFamily> };
const MODAL_INTERCHANGE: { of: Diatonic; chords: Borrowed[] }[] = [
  {
    of: { degree: 0, family: '∆' },
    chords: [
      { degree: 0, type: 'min7', roman: 'Im7' },
      { degree: 0, type: 'min6', roman: 'Im6' },
      { degree: 0, type: 'minmaj7', roman: 'Im∆7' },
    ],
  },
  {
    of: { degree: 2, family: 'm' },
    chords: [
      { degree: 2, type: 'min7b5', roman: 'IIø' },
      { degree: 1, type: 'maj7', roman: '♭II∆' },
    ],
  },
  { of: { degree: 4, family: 'm' }, chords: [{ degree: 3, type: 'maj7', roman: '♭III∆' }] },
  {
    of: { degree: 5, family: '∆' },
    chords: [
      { degree: 5, type: 'min6', roman: 'IVm6' },
      { degree: 5, type: 'min7', roman: 'IVm7' },
    ],
  },
  {
    of: { degree: 7, family: '7' },
    chords: [
      { degree: 7, type: 'dom7b9', roman: 'V7♭9' },
      { degree: 7, type: 'min7', roman: 'Vm7' },
    ],
  },
  { of: { degree: 9, family: 'm' }, chords: [{ degree: 8, type: 'maj7', roman: '♭VI∆' }] },
  { of: { degree: 11, family: 'ø' }, chords: [{ degree: 10, type: 'dom7', roman: '♭VII7' }] },
];
const ALL_BORROWED: Borrowed[] = MODAL_INTERCHANGE.flatMap((row) => row.chords);

const modalInterchange: ReharmMove = {
  id: 'modal-interchange',
  rung: 4,
  name: 'Borrowed chord',
  hint: 'A chord borrowed from the parallel minor.',
  source: 'Felts ch. 5 (modal interchange); Berklee Book of Jazz Harmony ch. 5.',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const region = regionAt(ctx, at);
    if (!region || region.minor || heardFromBass(ctx, at)) return [];
    const degree = mod12(pitchClassOf(chord.root) - region.tonicPc);
    const family = qualityFamily(chord.chordType);
    const next = ctx.sequence[at + 1];
    const beats = ctx.chordBeats[at];
    const offered = new Set<string>();
    const out: Proposal[] = [];
    const offer = (b: Borrowed, why: string) => {
      if (b.degree === degree && qualityFamily(b.type) === family) return;
      const key = `${b.degree}:${b.type}`;
      if (offered.has(key)) return;
      offered.add(key);
      out.push(
        proposal(
          ctx,
          modalInterchange,
          at,
          [{ root: spellRoot(mod12(region.tonicPc + b.degree), region), chordType: b.type, beats }],
          why,
        ),
      );
    };
    // Rule 1: the borrowed chords that REPLACE this diatonic chord.
    const row = MODAL_INTERCHANGE.find((r) => r.of.degree === degree && r.of.family === family);
    for (const b of row?.chords ?? []) offer(b, `${b.roman} from ${region.label} minor`);
    // Rule 2: a borrowed chord that LINKS into the next chord — Felts admits
    // any strong root motion (not a third or sixth); this takes the CADENTIAL
    // subset, a fourth or fifth either way or a half step down, and never a chord on the
    // tonic (a tonic-minor chord re-hears home, it does not approach
    // anything), or eight of the twelve qualify at every chord and the list
    // stops being a list of choices.
    if (next) {
      const nextPc = heard(ctx, at + 1).rootPc;
      for (const b of ALL_BORROWED) {
        if (b.degree === 0) continue;
        const pc = mod12(region.tonicPc + b.degree);
        const motion = mod12(nextPc - pc);
        if (motion !== 5 && motion !== 7 && motion !== 11) continue;
        offer(b, `${b.roman} from ${region.label} minor, into ${next.symbol}`);
      }
    }
    return out;
  },
};

/** Felts's turnaround catalogue (ch. 8) in scale degrees over the tonic: an
 *  opener from the tonic family or a chromatic stand-in for one, a link by
 *  strong root motion, a subdominant, a dominant into the top. Drawn over two
 *  bars in the book; here a layer cannot merge bars, so the whole figure is
 *  dealt across the ONE final tonic bar, a chord per beat. */
type Slot = { degree: number; type: ChordTypeId };
const TURNAROUNDS: { chords: Slot[]; why: string }[] = [
  {
    chords: [
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: 'ii–V back to the top',
  },
  {
    chords: [
      { degree: 0, type: 'maj7' },
      { degree: 9, type: 'dom7' },
    ],
    why: 'I then VI7, into the ii',
  },
  {
    chords: [
      { degree: 4, type: 'min7' },
      { degree: 9, type: 'dom7' },
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: 'iii VI ii V, the usual turnaround',
  },
  {
    chords: [
      { degree: 9, type: 'min7' },
      { degree: 2, type: 'dom7' },
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: 'vi then II7 into the ii–V',
  },
  {
    chords: [
      { degree: 9, type: 'min7' },
      { degree: 8, type: 'dom7' },
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: 'vi then ♭VI7 into the ii–V',
  },
  {
    chords: [
      { degree: 4, type: 'min7' },
      { degree: 9, type: 'dom7' },
      { degree: 2, type: 'min7' },
      { degree: 1, type: 'dom7' },
    ],
    why: 'iii VI ii then the tritone sub',
  },
  {
    chords: [
      { degree: 4, type: 'min7b5' },
      { degree: 9, type: 'dom7b9' },
      { degree: 2, type: 'min7' },
      { degree: 1, type: 'dom7' },
    ],
    why: 'iiiø VI7♭9 ii then the tritone sub',
  },
  {
    chords: [
      { degree: 4, type: 'dom7' },
      { degree: 9, type: 'dom7' },
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: 'III7 VI7 into the ii–V',
  },
  {
    chords: [
      { degree: 9, type: 'dom7' },
      { degree: 2, type: 'dom7' },
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: 'VI7 II7 into the ii–V',
  },
  {
    chords: [
      { degree: 9, type: 'dom7s9' },
      { degree: 8, type: 'dom7' },
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: 'VI7♯9 ♭VI7 into the ii–V',
  },
  {
    chords: [
      { degree: 3, type: 'maj7' },
      { degree: 8, type: 'maj7' },
      { degree: 1, type: 'maj7' },
    ],
    why: '♭III∆ ♭VI∆ ♭II∆, major sevenths down in fourths',
  },
  {
    chords: [
      { degree: 10, type: 'maj7' },
      { degree: 3, type: 'maj7' },
      { degree: 8, type: 'maj7' },
      { degree: 1, type: 'maj7' },
    ],
    why: '♭VII∆ ♭III∆ ♭VI∆ ♭II∆, major sevenths down in fourths',
  },
  {
    chords: [
      { degree: 1, type: 'maj7' },
      { degree: 2, type: 'min7' },
      { degree: 7, type: 'dom7' },
    ],
    why: '♭II∆ then the ii–V',
  },
  {
    chords: [
      { degree: 8, type: 'maj7' },
      { degree: 1, type: 'maj7' },
    ],
    why: '♭VI∆ ♭II∆ back to the top',
  },
  {
    chords: [
      { degree: 1, type: 'maj7' },
      { degree: 10, type: 'maj7' },
    ],
    why: '♭II∆ ♭VII∆ back to the top',
  },
  {
    chords: [
      { degree: 5, type: 'maj7' },
      { degree: 5, type: 'min7' },
      { degree: 10, type: 'dom7' },
    ],
    why: 'IV IVm7 ♭VII7, the backdoor',
  },
  {
    chords: [
      { degree: 6, type: 'min7b5' },
      { degree: 5, type: 'min7' },
      { degree: 10, type: 'dom7' },
    ],
    why: '♯IVø IVm7 ♭VII7, the backdoor',
  },
];

const turnback: ReharmMove = {
  id: 'turnback',
  rung: 4,
  name: 'Turnback',
  hint: 'A final tonic bar leads back round.',
  source: 'Felts ch. 8 (turnarounds).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const region = regionAt(ctx, at);
    if (!region || ctx.chordBeats[at] < 4 || heardFromBass(ctx, at)) return [];
    if (mod12(pitchClassOf(chord.root) - region.tonicPc) !== 0) return [];
    if (!['∆', 'm'].includes(qualityFamily(chord.chordType))) return [];
    // Only where the tonic hands over to the top — the last chord of the
    // form, or a tonic followed by the tonic again.
    const wraps = !ctx.sequence[at + 1] || mod12(heard(ctx, at + 1).rootPc - region.tonicPc) === 0;
    if (!wraps) return [];
    const spell = (degree: number) => spellRoot(mod12(region.tonicPc + degree), region);
    if (region.minor) {
      // The catalogue is a major-key one; a minor tonic keeps the two figures
      // that read the same in minor.
      const [a, b] = split(ctx, at, 2);
      return [
        proposal(
          ctx,
          turnback,
          at,
          [
            { root: spell(2), chordType: 'min7b5', beats: a },
            { root: spell(7), chordType: 'dom7', beats: b },
          ],
          'iiø–V back to the top',
        ),
        proposal(
          ctx,
          turnback,
          at,
          [restate(chord, a), { root: spell(9), chordType: 'dom7', beats: b }],
          'i then VI7, into the ii',
        ),
      ];
    }
    return TURNAROUNDS.map(({ chords, why }) => {
      const beats = dealBeats(ctx, at, chords.length);
      return proposal(
        ctx,
        turnback,
        at,
        chords.map((slot, i) =>
          // The tonic opener keeps the chord as written (a C6 stays a C6).
          slot.degree === 0
            ? restate(chord, beats[i])
            : { root: spell(slot.degree), chordType: slot.type, beats: beats[i] },
        ),
        why,
      );
    });
  },
};

// ---------------------------------------------------------------------------
// Rung 5
// ---------------------------------------------------------------------------

/** A diatonic pitch class's step neighbours in the key, walking from `from`
 *  toward `to` (exclusive), or null when either is not a scale tone. */
function scaleWalk(fromPc: number, toPc: number, degrees: readonly number[], dir: 1 | -1) {
  const from = degrees.indexOf(fromPc);
  const to = degrees.indexOf(toPc);
  if (from < 0 || to < 0) return null;
  const out: number[] = [];
  let i = from;
  for (let guard = 0; guard < degrees.length; guard++) {
    i = (i + dir + degrees.length) % degrees.length;
    if (i === to) return out;
    out.push(degrees[i]);
  }
  return null;
}

const bassLine: ReharmMove = {
  id: 'bass-line',
  rung: 5,
  name: 'Bass line',
  hint: 'A stepwise bass walks into the next chord, a chord on every step.',
  source: 'Felts ch. 6 (reharmonization using bass lines).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const next = ctx.sequence[at + 1];
    const region = regionAt(ctx, at);
    // The walk starts on the chord's root as its bass, and ends on the bass
    // the next chord is written over (F∆/A is reached on the A).
    if (!next || !region || ctx.chordBeats[at] < 3 || hasSlashBass(chord)) return [];
    const scale = diatonicSevenths(region.minor);
    const degreePcs = scale.map((d) => mod12(region.tonicPc + d.degree));
    const fromPc = pitchClassOf(chord.root);
    const toPc = pitchClassOf(next.bass ?? next.root);
    if (fromPc === toPc) return [];
    // Felts: usually descending; the shorter way where it is clearly shorter.
    const down = scaleWalk(fromPc, toPc, degreePcs, -1);
    const upWalk = scaleWalk(fromPc, toPc, degreePcs, 1);
    if (!down || !upWalk) return [];
    const steps = upWalk.length < down.length ? upWalk : down;
    // One to three passing bass notes: the chord keeps its downbeat, and a
    // line longer than the bar reads as a different tune.
    if (steps.length < 1 || steps.length > 3) return [];
    const rootedAt = (pc: number) => scale.find((d) => mod12(region.tonicPc + d.degree) === pc)!;
    const tonesOf = (rootPc: number, type: ChordTypeId) =>
      [0, ...CHORD_TYPES[type].intervals].map((iv) => mod12(rootPc + iv));
    const beats = dealBeats(ctx, at, steps.length + 1);
    const opener = restate(chord, beats[0]);
    const out: Proposal[] = [];
    const seen = new Set<string>();
    const emit = (line: ReharmChordSpec[], why: string) => {
      const key = line.map((c) => `${c.root}${c.chordType}/${c.bass ?? ''}`).join(' ');
      if (seen.has(key)) return;
      seen.add(key);
      out.push(proposal(ctx, bassLine, at, [opener, ...line], why));
    };

    // Inversions: the ringing chord stays while the bass walks under it
    // wherever the bass note is its 3rd or 5th — its 7th only straight after
    // root position (Felts: "C/B♭ will be heard as a B♭ chord… unless it has
    // a C7 before it") — else the diatonic chord rooted on the step.
    let prevRoot = fromPc;
    let prevType: ChordTypeId = chord.chordType;
    let prevRootPosition = true;
    const inversions: ReharmChordSpec[] = [];
    steps.forEach((pc, i) => {
      const tones = tonesOf(prevRoot, prevType);
      const asSeventh =
        tones.indexOf(pc) === 3 ||
        mod12(pc - prevRoot) === CHORD_TYPES[prevType].intervals.at(-1)! % 12;
      const keeps = tones.includes(pc) && pc !== prevRoot && (!asSeventh || prevRootPosition);
      if (keeps) {
        inversions.push({
          root: spellRoot(prevRoot, region),
          chordType: prevType,
          bass: spellRoot(pc, region),
          beats: beats[i + 1],
        });
        prevRootPosition = false;
      } else {
        const d = rootedAt(pc);
        inversions.push({ root: spellRoot(pc, region), chordType: d.type, beats: beats[i + 1] });
        prevRoot = pc;
        prevType = d.type;
        prevRootPosition = true;
      }
    });
    emit(inversions, `bass walks ${steps.length < 2 ? 'a step' : 'stepwise'} into ${next.symbol}`);

    // Roots: the diatonic chord on every step of the line.
    emit(
      steps.map((pc, i) => ({
        root: spellRoot(pc, region),
        chordType: rootedAt(pc).type,
        beats: beats[i + 1],
      })),
      `a chord on each step down to ${next.symbol}`,
    );

    // Dominants: a step that cadences onto the next bass note (a fifth or a
    // half step above it) carries the dominant rooted there.
    emit(
      steps.map((pc, i) => {
        const following = i + 1 < steps.length ? steps[i + 1] : toPc;
        const motion = mod12(following - pc);
        const dominant = motion === 5 || motion === 11;
        return {
          root: spellRoot(pc, region),
          chordType: dominant ? 'dom7' : rootedAt(pc).type,
          beats: beats[i + 1],
        };
      }),
      `dominants on the way down to ${next.symbol}`,
    );
    return out;
  },
};

const pedalPoint: ReharmMove = {
  id: 'pedal',
  rung: 5,
  name: 'Pedal point',
  hint: 'The bass holds while the chord above it moves.',
  source: 'Felts ch. 6 (reharmonization using bass lines: pedal point, chords over their 5th).',
  candidates(ctx, at) {
    const chord = ctx.sequence[at];
    const out: Proposal[] = [];
    // A pedal holds the chord's own root (or its fifth) in the bass; a chord
    // already written over another bass has one.
    if (hasSlashBass(chord)) return out;
    const region = regionAt(ctx, at);
    // The chord over its own FIFTH for its whole span (C∆/G): the bass pedals
    // the dominant note under a held chord — LaVerne opens Green Dolphin
    // Street on two bars of it. Any chord with a perfect fifth, two beats up.
    const hasFifth = CHORD_TYPES[chord.chordType].intervals.some((iv) => iv % 12 === 7);
    if (hasFifth && ctx.chordBeats[at] >= 2) {
      const fifth = spellRoot(mod12(pitchClassOf(chord.root) + 7), region);
      out.push(
        proposal(
          ctx,
          pedalPoint,
          at,
          [
            {
              root: chord.displayRoot,
              chordType: chord.chordType,
              bass: fifth,
              beats: ctx.chordBeats[at],
            },
          ],
          `${chord.symbol} over its 5th`,
        ),
      );
    }
    if (ctx.chordBeats[at] < 4) return out;
    const family = qualityFamily(chord.chordType);
    if (!['∆', 'm'].includes(family)) return out;
    const [a, b] = split(ctx, at, 2);
    const bass = chord.displayRoot;
    // Over a major chord the IV above the pedal (C∆ | F/C); over a minor
    // chord the ♭VII (Cm7 | B♭/C) — both the classic pedal figures.
    const upperPc = mod12(pitchClassOf(chord.root) + (family === '∆' ? 5 : 10));
    const upperType: ChordTypeId = family === '∆' ? 'maj7' : 'dom7';
    const upper = spellRoot(upperPc, region);
    out.push(
      proposal(
        ctx,
        pedalPoint,
        at,
        [restate(chord, a), { root: upper, chordType: upperType, bass, beats: b }],
        `${symbolOf({ root: upper, chordType: upperType, bass, beats: 0 })} over the ${bass} pedal`,
      ),
    );
    return out;
  },
};

export const RUNG_3_TO_5_MOVES: readonly ReharmMove[] = [
  passingDim,
  dimForDominant,
  dominantForDim,
  chromaticApproach,
  sixthDiminished,
  lineCliche,
  modalInterchange,
  turnback,
  bassLine,
  pedalPoint,
];
